// virtual-medic-frontend/src/components/CaseManager/CaseViewer.jsx
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Paper,
  Grid,
  Chip,
  Button,
  Card,
  CardContent,
  CardMedia,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  CircularProgress,
  Divider,
  Dialog,
  DialogContent,
  DialogActions,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  IconButton,
  MenuItem,
  Select,
  Switch,
  Stack,
  Tooltip,
  Checkbox
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Publish as PublishIcon,
  ExpandMore as ExpandMoreIcon,
  Person as PersonIcon,
  Psychology as PsychologyIcon,
  Quiz as QuizIcon,
  Close as CloseIcon,
  ZoomIn as ZoomInIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import api, { communityAPI } from '../../services/api';
import { getCharacterImageUrl, getQuestionMediaUrl } from '../../utils/mediaUrl';
import { getCurrentUser } from '../../services/auth';
import { isGlobalAdmin, isTenantScopedAdmin, isVirtualMedicInstructor } from '../../utils/role';
import { getApplicableCategories, resolveEffectiveWeights } from '../../utils/scoringCategories';

// Helper function to construct proper media URLs
const getMediaUrl = (file) => {
  if (file.url) {
    // If the backend already provides a full URL, use it
    return file.url;
  }
  if (file.path) {
    // Otherwise, construct it using our utility
    return getQuestionMediaUrl(file.path);
  }
  return '';
};

const scoringFieldMetadata = [
  { key: 'historyQuestions', label: 'History Questions' },
  { key: 'flow', label: 'Flow' },
  { key: 'examinerQuestions', label: 'Examiner Questions' },
  { key: 'empathy', label: 'Empathy' }
];

const disclosureStyleLabels = {
  guarded: 'Guarded',
  standard: 'Standard OSCE',
  forthcoming: 'Forthcoming',
};

const CaseViewer = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [case_data, setCaseData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [lightbox, setLightbox] = useState({ open: false, src: '', title: '' });
  const currentUser = useMemo(() => getCurrentUser(), []);
  const isUniversityUser = currentUser?.accountType === 'university';
  const isPrivilegedUser = isTenantScopedAdmin(currentUser)
    || isVirtualMedicInstructor(currentUser)
    || isGlobalAdmin(currentUser);
  const isIndividualUser = currentUser?.accountType === 'individual' && !isPrivilegedUser;
  const [communityActionError, setCommunityActionError] = useState('');
  const [communityDialog, setCommunityDialog] = useState({
    open: false,
    scope: isUniversityUser ? 'tenant' : 'global',
    licenseAccepted: false,
    allowComments: true,
    allowRatings: true,
    submitting: false,
    error: ''
  });
  const autoOpenedRef = useRef(false);

  const scoringConfig = case_data?.scoringConfiguration;
  // Only the categories this case marks are weighted; the server reports them
  // with the weights rescaled to 100%.
  const applicableScoringCategories = Array.isArray(scoringConfig?.applicableCategories)
    ? scoringConfig.applicableCategories
    : getApplicableCategories(case_data || {});
  const scoringFields = scoringFieldMetadata
    .filter((field) => applicableScoringCategories.includes(field.key));
  const scoringResolved = resolveEffectiveWeights(
    scoringConfig?.resolved || {},
    applicableScoringCategories,
    scoringConfig?.defaults || {}
  );
  const scoringDefaults = resolveEffectiveWeights(
    scoringConfig?.defaults || {},
    applicableScoringCategories,
    scoringConfig?.defaults || {}
  );
  const scoringOverrides = scoringConfig?.overrides || null;
  const scoringTotal = scoringFields.reduce((sum, field) => (
    sum + Number(scoringResolved[field.key] || 0)
  ), 0);
  const scoringIsBalanced = !scoringFields.length || Math.abs(scoringTotal - 1) < 0.001;
  const isCommunityEligible = useMemo(() => (
    case_data
    && case_data.library?.scope !== 'official'
    && case_data.provenance?.originType !== 'virtual-medic'
  ), [case_data]);

  useEffect(() => {
    loadCase();
    // The route identifier is the only intended reload trigger.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (autoOpenedRef.current) {
      return;
    }
    const shouldOpen = searchParams.get('publishCommunity');
    if (!shouldOpen || shouldOpen.toString().toLowerCase() === 'false') {
      return;
    }
    if (!case_data || !isCommunityEligible) {
      return;
    }
    autoOpenedRef.current = true;
    openCommunityDialog();
    // The dialog helper consumes the case values already represented below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, case_data, isCommunityEligible]);

  const loadCase = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/cases/${id}`);
      setCaseData(response.data);
    } catch (err) {
      console.error('Error loading case:', err);
      setError('Failed to load case details');
    } finally {
      setLoading(false);
    }
  };

  const openCommunityDialog = () => {
    const community = case_data?.community || {};
    setCommunityDialog({
      open: true,
      scope: community.scope || (isUniversityUser ? 'tenant' : 'global'),
      licenseAccepted: community.licenseAccepted || false,
      allowComments: community.allowComments !== false,
      allowRatings: community.allowRatings !== false,
      submitting: false,
      error: ''
    });
  };

  const handlePublishCommunity = async () => {
    if (!case_data) {
      return;
    }

    if (!communityDialog.licenseAccepted) {
      setCommunityDialog((prev) => ({
        ...prev,
        error: 'You must accept the community license before publishing.'
      }));
      return;
    }

    setCommunityDialog((prev) => ({ ...prev, submitting: true, error: '' }));
    try {
      await communityAPI.publishCase(case_data._id, {
        scope: communityDialog.scope,
        licenseAccepted: communityDialog.licenseAccepted,
        allowComments: communityDialog.allowComments,
        allowRatings: communityDialog.allowRatings
      });
      setCommunityDialog((prev) => ({ ...prev, open: false, submitting: false }));
      setCommunityActionError('');
      loadCase();
    } catch (err) {
      const message = err.response?.data?.message || 'Unable to publish to community.';
      setCommunityDialog((prev) => ({ ...prev, submitting: false, error: message }));
    }
  };

  const handleWithdrawCommunity = async ({ closeDialog } = {}) => {
    if (!case_data) {
      return;
    }

    setCommunityActionError('');
    try {
      await communityAPI.withdrawCase(case_data._id);
      if (closeDialog) {
        setCommunityDialog((prev) => ({ ...prev, open: false, error: '' }));
      }
      loadCase();
    } catch (err) {
      const message = err.response?.data?.message || 'Unable to withdraw community listing.';
      setCommunityActionError(message);
    }
  };

  const openLightbox = (src, title) => {
    setLightbox({ open: true, src, title });
  };

  const closeLightbox = () => {
    setLightbox({ open: false, src: '', title: '' });
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'Core': return 'success';
      case 'Intermediate': return 'warning';
      case 'Advanced': return 'error';
      default: return 'default';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'testing': return 'warning';
      case 'review': return 'info';
      case 'approved': return 'success';
      case 'archived': return 'error';
      default: return 'default';
    }
  };

  const getCommunityStateColor = (state) => {
    switch (state) {
      case 'published': return 'success';
      case 'pending': return 'warning';
      case 'rejected': return 'error';
      case 'archived': return 'default';
      default: return 'default';
    }
  };

  const isPublishedAny = () => (
    Boolean(case_data?.environments?.testing?.published || case_data?.environments?.production?.published)
  );

  const getLatestPublishedAt = () => {
    const timestamps = [
      case_data?.environments?.testing?.publishedAt,
      case_data?.environments?.production?.publishedAt,
    ]
      .filter(Boolean)
      .map((value) => new Date(value))
      .filter((date) => !Number.isNaN(date.getTime()));

    if (!timestamps.length) {
      return null;
    }

    return new Date(Math.max(...timestamps.map((date) => date.getTime())));
  };

  const isPublished = (environment) => {
    return case_data?.environments?.[environment]?.published || false;
  };

  if (loading) {
    return (
      <Container>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
          <Typography sx={{ ml: 2 }}>Loading case details...</Typography>
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container sx={{ py: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  if (!case_data) {
    return (
      <Container sx={{ py: 4 }}>
        <Alert severity="info">Case not found</Alert>
      </Container>
    );
  }

  const community = case_data.community || {};
  const communityListingState = community.listingState || 'draft';
  const communityDialogTitle = (communityListingState === 'published' || communityListingState === 'pending')
    ? 'Manage community listing'
    : 'Publish to community';

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
        <Box display="flex" alignItems="center">
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/cases')}
            sx={{ mr: 2 }}
          >
            Back to Cases
          </Button>
          <Typography variant="h4" component="h1">
            {case_data.title}
          </Typography>
        </Box>

        <Button
          variant="contained"
          startIcon={<EditIcon />}
          onClick={() => navigate(`/cases/${id}/edit`)}
          sx={{
            bgcolor: '#16a085',
            '&:hover': { bgcolor: '#1abc9c' }
          }}
        >
          Edit Case
        </Button>
      </Box>

      <Grid container spacing={4}>
        {/* Case Overview */}
        <Grid item xs={12} lg={4}>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              <PersonIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Case Overview
            </Typography>

            <Box mb={2}>
              <Typography variant="body2" color="text.secondary">Patient Name</Typography>
              <Typography variant="body1">{case_data.patientName}</Typography>
            </Box>

            <Box mb={2}>
              <Typography variant="body2" color="text.secondary">Demographics</Typography>
              <Typography variant="body1">
                {case_data.gender}, {case_data.age} years old
              </Typography>
              <Typography variant="body2" color="text.secondary">
                DOB: {format(new Date(case_data.dateOfBirth), 'MMM dd, yyyy')}
              </Typography>
            </Box>

            <Box mb={2}>
              <Typography variant="body2" color="text.secondary">Presenting Complaint</Typography>
              <Chip label={case_data.presentingComplaint} color="primary" />
            </Box>

            <Box mb={2}>
              <Typography variant="body2" color="text.secondary">Specialty</Typography>
              <Chip label={case_data.specialty} variant="outlined" />
            </Box>

            <Box mb={2}>
              <Typography variant="body2" color="text.secondary">Difficulty</Typography>
              <Chip
                label={case_data.difficulty}
                color={getDifficultyColor(case_data.difficulty)}
              />
            </Box>

            <Box mb={2}>
              <Typography variant="body2" color="text.secondary">Status</Typography>
              <Chip
                label={case_data.status}
                color={getStatusColor(case_data.status)}
              />
            </Box>

            {case_data.shortDescription && (
              <Box mb={2}>
                <Typography variant="body2" color="text.secondary">Description</Typography>
                <Typography variant="body1">{case_data.shortDescription}</Typography>
              </Box>
            )}

            <Divider sx={{ my: 2 }} />

            <Box mb={2}>
              <Typography variant="body2" color="text.secondary">Author</Typography>
              <Typography variant="body1">
                {case_data.author?.firstName} {case_data.author?.lastName}
              </Typography>
            </Box>

            <Box mb={2}>
              <Typography variant="body2" color="text.secondary">Created</Typography>
              <Typography variant="body1">
                {format(new Date(case_data.createdAt), 'MMM dd, yyyy HH:mm')}
              </Typography>
            </Box>

            <Box mb={2}>
              <Typography variant="body2" color="text.secondary">Last Updated</Typography>
              <Typography variant="body1">
                {format(new Date(case_data.updatedAt), 'MMM dd, yyyy HH:mm')}
              </Typography>
            </Box>

            <Box>
              <Typography variant="body2" color="text.secondary">Version</Typography>
              <Typography variant="body1">v{case_data.version}</Typography>
            </Box>
          </Paper>

          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              <PublishIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Community Listing
            </Typography>

            <Stack spacing={1}>
              <Box>
                <Typography variant="body2" color="text.secondary">Status</Typography>
                <Chip
                  label={communityListingState}
                  color={getCommunityStateColor(communityListingState)}
                  variant="outlined"
                />
              </Box>

              {community.scope && (
                <Box>
                  <Typography variant="body2" color="text.secondary">Scope</Typography>
                  <Typography variant="body1">
                    {community.scope === 'tenant' ? 'University community' : 'Global community'}
                  </Typography>
                </Box>
              )}

              {community.publishedAt && (
                <Box>
                  <Typography variant="body2" color="text.secondary">Published</Typography>
                  <Typography variant="body1">
                    {format(new Date(community.publishedAt), 'MMM dd, yyyy HH:mm')}
                  </Typography>
                </Box>
              )}

              {community.approvalNotes && (
                <Alert severity="info">{community.approvalNotes}</Alert>
              )}

              {communityActionError && (
                <Alert severity="error">{communityActionError}</Alert>
              )}

              {!isCommunityEligible && (
                <Alert severity="info">
                  Official library cases cannot be published to the community.
                </Alert>
              )}

              {isCommunityEligible && (
                <Stack spacing={1}>
                  <Button
                    variant={communityListingState === 'published' || communityListingState === 'pending' ? 'outlined' : 'contained'}
                    onClick={openCommunityDialog}
                  >
                    {(communityListingState === 'published' || communityListingState === 'pending')
                      ? 'Manage community listing'
                      : 'Publish to community'}
                  </Button>
                  {communityListingState === 'pending' && (
                    <Typography variant="body2" color="text.secondary">
                      This listing is awaiting faculty approval.
                    </Typography>
                  )}
                  {communityListingState === 'rejected' && (
                    <Typography variant="body2" color="text.secondary">
                      This listing was rejected. Update your case and resubmit when ready.
                    </Typography>
                  )}
                </Stack>
              )}
            </Stack>
          </Paper>

          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              <QuizIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Scoring Configuration
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Review the weighting applied to each rubric category when calculating overall scores.
            </Typography>

            {scoringConfig ? (
              <>
                <Stack spacing={1.5} sx={{ mb: 2 }}>
                  {scoringFields.map((field) => {
                    const defaultValue = scoringDefaults[field.key] ?? 0;
                    const resolvedValue = scoringResolved[field.key] ?? defaultValue;
                    const delta = resolvedValue - defaultValue;
                    return (
                      <Box key={field.key} display="flex" justifyContent="space-between" alignItems="center">
                        <Box>
                          <Typography variant="subtitle2">{field.label}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            Default: {(defaultValue * 100).toFixed(1)}%
                          </Typography>
                        </Box>
                        <Box display="flex" alignItems="center" gap={1}>
                          {Math.abs(delta) > 0.0005 && (
                            <Tooltip title="Difference from global default">
                              <Chip
                                size="small"
                                color={delta > 0 ? 'success' : 'warning'}
                                label={`${delta > 0 ? '+' : ''}${(delta * 100).toFixed(1)}%`}
                              />
                            </Tooltip>
                          )}
                          <Typography variant="body2" fontWeight="bold">
                            {(resolvedValue * 100).toFixed(1)}%
                          </Typography>
                        </Box>
                      </Box>
                    );
                  })}
                </Stack>

                <Alert severity={scoringIsBalanced ? (scoringOverrides ? 'info' : 'success') : 'warning'}>
                  {scoringOverrides ? 'Case-specific overrides are active.' : 'Using global default weights.'}
                  <br />
                  Total weight: {(scoringTotal * 100).toFixed(1)}%
                </Alert>
              </>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Scoring weights have not been configured for this case yet.
              </Typography>
            )}
          </Paper>

          {/* Character Information */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              <PsychologyIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Character & Voice Configuration
            </Typography>

            {case_data.characterId && (
              <Box mb={2}>
                <Box display="flex" alignItems="center" mb={1}>
                  {case_data.characterId.profilePicture && (
                    <CardMedia
                      component="img"
                      sx={{ width: 60, height: 60, borderRadius: 1, mr: 2 }}
                      image={getCharacterImageUrl(case_data.characterId.profilePicture)}
                      alt={case_data.characterId.name}
                    />
                  )}
                  <Box>
                    <Typography variant="subtitle1">{case_data.characterId.name}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {case_data.characterId.addressableKey}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            )}

            <Box mb={2}>
              <Typography variant="body2" color="text.secondary">OpenAI Voice Model</Typography>
              <Typography variant="body1">{case_data.openAIVoiceModel || case_data.voiceModel || 'ash'}</Typography>
            </Box>

            <Box mb={2}>
              <Typography variant="body2" color="text.secondary">Gemini Voice Model</Typography>
              <Typography variant="body1">{case_data.geminiVoiceModel || 'Kore'}</Typography>
            </Box>

            {/* Advanced Voice Configuration */}
            {(case_data.patientDisclosureStyle || case_data.characterPersonality || case_data.characterAccent || case_data.characterSpeechStyle) && (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle1" gutterBottom>
                  Advanced Voice Configuration
                </Typography>

                <Box mb={2}>
                  <Typography variant="body2" color="text.secondary">Information Disclosure</Typography>
                  <Typography variant="body1">
                    {disclosureStyleLabels[case_data.patientDisclosureStyle] || disclosureStyleLabels.standard}
                  </Typography>
                </Box>

                {/* Personality */}
                {case_data.characterPersonality && (
                  <Box mb={2}>
                    <Typography variant="body2" color="text.secondary">Patient Personality</Typography>
                    <Typography variant="body1">
                      {case_data.characterPersonality === 'custom'
                        ? (case_data.characterPersonalityCustom || 'Custom personality (not specified)')
                        : case_data.characterPersonality
                      }
                    </Typography>
                  </Box>
                )}

                {/* Accent/Speech Pattern */}
                {case_data.characterAccent && (
                  <Box mb={2}>
                    <Typography variant="body2" color="text.secondary">Accent/Speech Pattern</Typography>
                    <Typography variant="body1">
                      {case_data.characterAccent === 'custom'
                        ? (case_data.characterAccentCustom || 'Custom accent (not specified)')
                        : case_data.characterAccent
                      }
                    </Typography>
                  </Box>
                )}

                {/* Additional Speech Instructions */}
                {case_data.characterSpeechStyle && (
                  <Box mb={2}>
                    <Typography variant="body2" color="text.secondary">Additional Speech Instructions</Typography>
                    <Typography variant="body1">{case_data.characterSpeechStyle}</Typography>
                  </Box>
                )}
              </>
            )}
          </Paper>

          {/* Publication Status */}
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              <PublishIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Publication Status
            </Typography>

            {isIndividualUser ? (
              <Box>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2">Library availability</Typography>
                  <Chip
                    label={isPublishedAny() ? 'Published' : 'Not Published'}
                    color={isPublishedAny() ? 'success' : 'default'}
                    size="small"
                  />
                </Box>
                {isPublishedAny() && getLatestPublishedAt() && (
                  <Typography variant="caption" color="text.secondary">
                    Published: {format(getLatestPublishedAt(), 'MMM dd, yyyy HH:mm')}
                  </Typography>
                )}
              </Box>
            ) : (
              <>
                <Box mb={2}>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="body2">Testing Environment</Typography>
                    <Chip
                      label={isPublished('testing') ? 'Published' : 'Not Published'}
                      color={isPublished('testing') ? 'success' : 'default'}
                      size="small"
                    />
                  </Box>
                  {isPublished('testing') && case_data.environments.testing.publishedAt && (
                    <Typography variant="caption" color="text.secondary">
                      Published: {format(new Date(case_data.environments.testing.publishedAt), 'MMM dd, yyyy HH:mm')}
                    </Typography>
                  )}
                </Box>

                <Box>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="body2">Production Environment</Typography>
                    <Chip
                      label={isPublished('production') ? 'Published' : 'Not Published'}
                      color={isPublished('production') ? 'primary' : 'default'}
                      size="small"
                    />
                  </Box>
                  {isPublished('production') && case_data.environments.production.publishedAt && (
                    <Typography variant="caption" color="text.secondary">
                      Published: {format(new Date(case_data.environments.production.publishedAt), 'MMM dd, yyyy HH:mm')}
                    </Typography>
                  )}
                </Box>
              </>
            )}
          </Paper>
        </Grid>

        {/* Mark Scheme */}
        <Grid item xs={12} lg={8}>
          {case_data.patientNarrative?.trim() && (
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h5" gutterBottom>
                Patient Narrative
              </Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                {case_data.patientNarrative}
              </Typography>
            </Paper>
          )}

          {case_data.examinationFindings?.trim() && (
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h5" gutterBottom>
                Examination Findings
              </Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                {case_data.examinationFindings}
              </Typography>
            </Paper>
          )}

          {/* Sections */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h5" gutterBottom>
              Mark Scheme
            </Typography>

            {case_data.sections && case_data.sections.length > 0 ? (
              case_data.sections
                .sort((a, b) => a.order - b.order)
                .map((section) => (
                  <Accordion key={section._id} sx={{ mb: 1 }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Box display="flex" alignItems="center">
                        <Typography variant="h6">{section.title}</Typography>
                        <Chip
                          label={section.sectionType}
                          size="small"
                          sx={{ ml: 2 }}
                          color={section.isCustom ? 'secondary' : 'primary'}
                        />
                        <Chip
                          label={`${section.items?.length || 0} items`}
                          size="small"
                          sx={{ ml: 1 }}
                          variant="outlined"
                        />
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      {section.items && section.items.length > 0 ? (
                        <TableContainer>
                          <Table>
                            <TableHead>
                              <TableRow>
                                <TableCell>ID</TableCell>
                                <TableCell>Title</TableCell>
                                <TableCell>Max Marks</TableCell>
                                <TableCell>Marking Criteria</TableCell>
                                <TableCell>Patient Notes</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {section.items
                                .sort((a, b) => a.order - b.order)
                                .map((item) => (
                                  <TableRow key={item._id}>
                                    <TableCell>{item.itemId}</TableCell>
                                    <TableCell>{item.title}</TableCell>
                                    <TableCell>{item.maxMarks}</TableCell>
                                    <TableCell>
                                      <Typography variant="body2">
                                        {item.markingCriteria}
                                      </Typography>
                                      {item.extendedInstructions && (
                                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                                          Additional: {item.extendedInstructions}
                                        </Typography>
                                      )}
                                    </TableCell>
                                    <TableCell>
                                      <Typography variant="body2" color={item.patientResponse?.trim() ? 'text.primary' : 'text.secondary'}>
                                        {item.patientResponse?.trim() || '—'}
                                      </Typography>
                                    </TableCell>
                                  </TableRow>
                                ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      ) : (
                        <Typography color="text.secondary">No items in this section</Typography>
                      )}
                    </AccordionDetails>
                  </Accordion>
                ))
            ) : (
              <Alert severity="info">No case content sections defined</Alert>
            )}
          </Paper>

          {/* Examiner Questions */}
          <Paper sx={{ p: 3 }}>
            <Typography variant="h5" gutterBottom>
              <QuizIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Examiner Questions
            </Typography>

            {case_data.examinerQuestions && case_data.examinerQuestions.length > 0 ? (
              case_data.examinerQuestions
                .sort((a, b) => a.order - b.order)
                .map((question, index) => (
                  <Card key={question._id} sx={{ mb: 2 }}>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Question {index + 1}
                        <Chip
                          label={`${question.maxMarks} marks`}
                          size="small"
                          sx={{ ml: 2 }}
                          color="primary"
                        />
                      </Typography>

                      <Typography variant="body1" sx={{ mb: 2 }}>
                        {question.question}
                      </Typography>

                      <Box mb={2}>
                        <Typography variant="body2" color="text.secondary">Correct Answer:</Typography>
                        <Typography variant="body1">{question.correctAnswer}</Typography>
                      </Box>

                      <Box mb={2}>
                        <Typography variant="body2" color="text.secondary">Marking Instructions:</Typography>
                        <Typography variant="body2">{question.markingInstructions}</Typography>
                      </Box>

                      {question.mediaFiles && question.mediaFiles.length > 0 && (
                        <Box>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            Media Files:
                          </Typography>
                          <Grid container spacing={1}>
                            {question.mediaFiles.map((file, fileIndex) => (
                              <Grid item key={fileIndex}>
                                <Card sx={{ width: 120, cursor: 'pointer' }}
                                  onClick={() => openLightbox(getMediaUrl(file), file.originalName)}>
                                  <CardMedia
                                    component="img"
                                    height="80"
                                    image={getMediaUrl(file)}
                                    alt={file.originalName || file.filename}
                                    sx={{ objectFit: 'cover' }}
                                  />
                                  <Box sx={{ p: 0.5, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <ZoomInIcon sx={{ fontSize: 16, mr: 0.5 }} />
                                    <Typography variant="caption" noWrap>
                                      {file.originalName || file.filename}
                                    </Typography>
                                  </Box>
                                </Card>
                              </Grid>
                            ))}
                          </Grid>
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                ))
            ) : (
              <Alert severity="info">No examiner questions defined</Alert>
            )}
          </Paper>
        </Grid>
      </Grid>

      <Dialog
        open={communityDialog.open}
        onClose={() => setCommunityDialog((prev) => ({ ...prev, open: false, error: '' }))}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{communityDialogTitle}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {(communityListingState === 'published' || communityListingState === 'pending') && (
              <Alert severity="info">
                This case already has a community listing. Update the details below or remove the listing.
              </Alert>
            )}
            <FormControl fullWidth>
              <InputLabel>Scope</InputLabel>
              <Select
                value={communityDialog.scope}
                label="Scope"
                onChange={(event) => setCommunityDialog((prev) => ({ ...prev, scope: event.target.value }))}
                disabled={!isUniversityUser}
              >
                {isUniversityUser && (
                  <MenuItem value="tenant">University community</MenuItem>
                )}
                <MenuItem value="global">Global community</MenuItem>
              </Select>
            </FormControl>

            <FormControlLabel
              control={
                <Checkbox
                  checked={communityDialog.licenseAccepted}
                  onChange={(event) => setCommunityDialog((prev) => ({ ...prev, licenseAccepted: event.target.checked }))}
                />
              }
              label="I confirm I own the content and grant Virtual Medic permission to host and share it."
            />

            <Divider />

            <FormControlLabel
              control={
                <Switch
                  checked={communityDialog.allowComments}
                  onChange={(event) => setCommunityDialog((prev) => ({ ...prev, allowComments: event.target.checked }))}
                />
              }
              label="Allow comments"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={communityDialog.allowRatings}
                  onChange={(event) => setCommunityDialog((prev) => ({ ...prev, allowRatings: event.target.checked }))}
                />
              }
              label="Allow ratings"
            />

            {communityDialog.error && (
              <Alert severity="error">{communityDialog.error}</Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCommunityDialog((prev) => ({ ...prev, open: false, error: '' }))}>
            Cancel
          </Button>
          {(communityListingState === 'published' || communityListingState === 'pending') && (
            <Button
              color="error"
              onClick={() => handleWithdrawCommunity({ closeDialog: true })}
              disabled={communityDialog.submitting}
            >
              Remove listing
            </Button>
          )}
          <Button
            variant="contained"
            onClick={handlePublishCommunity}
            disabled={communityDialog.submitting}
          >
            {communityDialog.submitting ? 'Submitting...' : ((communityListingState === 'published' || communityListingState === 'pending') ? 'Save changes' : 'Publish')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Lightbox Dialog */}
      <Dialog
        open={lightbox.open}
        onClose={closeLightbox}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { backgroundColor: 'rgba(0, 0, 0, 0.9)' }
        }}
      >
        <DialogActions sx={{ position: 'absolute', top: 0, right: 0, zIndex: 1 }}>
          <IconButton onClick={closeLightbox} sx={{ color: 'white' }}>
            <CloseIcon />
          </IconButton>
        </DialogActions>
        <DialogContent sx={{ p: 0, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          {lightbox.src && (
            <img
              src={lightbox.src}
              alt={lightbox.title}
              style={{
                maxWidth: '100%',
                maxHeight: '90vh',
                objectFit: 'contain'
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </Container>
  );
};

export default CaseViewer;
